const VISIT_TIMEOUT = 30 * 60 * 1000;
const DISMISSAL_DAYS = 7;
const WAIT_MS = 10000;

export function currentVisit(saved, now) {
  if (!saved || now - saved.lastActivity >= VISIT_TIMEOUT) {
    return { lastActivity: now, activeMs: 0, shown: false, email: false };
  }
  return saved;
}

export function welcomeDecision(state) {
  const excluded = state.subscriber || state.purchaser || state.completed || state.email;
  return {
    automatic: !excluded && !state.blocked && !state.dismissed && !state.shown && state.activeMs >= WAIT_MS,
    manual: !excluded && !state.blocked && state.dismissed
  };
}

export async function submitSignup(form, request = fetch) {
  const response = await request(form.action, {
    method: 'POST', body: new FormData(form), credentials: 'same-origin',
    headers: { Accept: 'text/html' }
  });
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  if (new URL(response.url, location.href).pathname.includes('/challenge') || doc.querySelector('.shopify-challenge__container')) {
    return { challenge: true };
  }
  if (!response.ok) throw new Error('We couldn’t submit your email. Please try again.');
  const result = doc.querySelector('#theniya-email-form');
  if (result?.querySelector('[data-welcome-status="success"]')) return { success: true };
  const error = result?.querySelector('[data-welcome-status="error"]');
  throw new Error(error?.textContent.trim() || 'We couldn’t confirm your signup. Please try again.');
}

export function initWelcome() {
  const overlay = document.getElementById('theniya-popup-overlay');
  const form = document.getElementById('theniya-email-form');
  const opener = document.querySelector('[data-welcome-open]');
  if (!overlay || !form || !opener) return;
  const dialog = overlay.querySelector('[role="dialog"]');
  const closeButton = overlay.querySelector('.theniya-popup-close');
  const error = overlay.querySelector('[data-welcome-error]');
  const submit = form.querySelector('[type="submit"]');
  const thankYou = overlay.querySelector('.theniya-popup-thankyou');
  const memory = {};
  const get = key => { try { return localStorage.getItem(key) ?? memory[key]; } catch { return memory[key]; } };
  const set = (key, value) => { memory[key] = value; try { localStorage.setItem(key, value); } catch {} };
  const cookie = name => document.cookie.split(';').some(value => value.trim().startsWith(`${name}=`));
  const signedSubscriber = overlay.dataset.subscriber === 'true';
  const signedPurchaser = overlay.dataset.purchaser === 'true';
  if (signedSubscriber) set('theniya_welcome_signup', '1');
  if (signedPurchaser) set('theniya_welcome_purchaser', '1');
  const readVisit = () => {
    let saved;
    try { saved = JSON.parse(get('theniya_welcome_visit')); } catch {}
    return currentVisit(saved, Date.now());
  };
  let visit = readVisit();
  const params = new URLSearchParams(location.search);
  if ((params.get('utm_medium') || '').toLowerCase() === 'email' || (params.get('utm_source') || '').toLowerCase() === 'shopify_email') visit.email = true;
  set('theniya_welcome_visit', JSON.stringify(visit));
  let active = false;
  let previousFocus;
  let busy = false;
  let lastTick = Date.now();
  let stopped = false;
  const blocked = () => overlay.dataset.pageType === 'cart' || Boolean(document.querySelector('cart-drawer.active, dialog[open], quick-add-modal[open], .menu-drawer-container[open]'));
  const state = () => ({
    subscriber: signedSubscriber,
    purchaser: signedPurchaser || get('theniya_welcome_purchaser') === '1',
    completed: get('theniya_welcome_signup') === '1',
    email: visit.email,
    dismissed: Number(get('theniya_welcome_dismiss_until')) > Date.now() || cookie('theniya_popup_shown'),
    shown: visit.shown, activeMs: visit.activeMs, blocked: blocked()
  });
  const refresh = () => { opener.hidden = active || !welcomeDecision(state()).manual; };
  const close = (dismiss = true) => {
    if (!active) return;
    overlay.classList.remove('active');
    overlay.setAttribute('aria-hidden', 'true');
    active = false;
    document.body.classList.remove('theniya-welcome-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(previousFocus?.isConnected ? previousFocus : null);
    else if (previousFocus?.isConnected) previousFocus.focus();
    if (dismiss && get('theniya_welcome_signup') !== '1') {
      set('theniya_welcome_dismiss_until', String(Date.now() + DISMISSAL_DAYS * 86400000));
      document.cookie = `theniya_popup_shown=true;Max-Age=${DISMISSAL_DAYS * 86400};Path=/;SameSite=Lax;Secure`;
    }
    refresh();
  };
  const open = manual => {
    const decision = welcomeDecision(state());
    if (active || !(manual ? decision.manual : decision.automatic)) return;
    previousFocus = document.activeElement;
    visit.shown = true;
    set('theniya_welcome_visit', JSON.stringify(visit));
    overlay.classList.add('active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('theniya-welcome-open');
    active = true;
    refresh();
    if (typeof trapFocus === 'function') trapFocus(dialog, closeButton);
    else closeButton.focus();
  };
  const success = () => {
    set('theniya_welcome_signup', '1');
    form.hidden = true;
    form.style.display = 'none';
    overlay.querySelector('.tagline')?.setAttribute('hidden', '');
    thankYou.hidden = false;
    thankYou.style.display = 'block';
    refresh();
  };
  if (form.querySelector('[data-welcome-status="success"]')) success();
  closeButton.addEventListener('click', () => close());
  overlay.addEventListener('click', event => { if (event.target === overlay) close(); });
  document.addEventListener('keydown', event => { if (event.key === 'Escape' && active) { event.preventDefault(); close(); } });
  opener.addEventListener('click', () => open(true));
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy || !form.reportValidity()) return;
    busy = true;
    error.textContent = '';
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    try {
      const result = await submitSignup(form);
      if (result.challenge) { HTMLFormElement.prototype.submit.call(form); return; }
      success();
    } catch (failure) {
      error.textContent = failure.message;
    } finally {
      busy = false;
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  });
  const timer = setInterval(() => {
    if (stopped) return;
    const now = Date.now();
    visit = readVisit();
    if (document.visibilityState === 'visible' && document.hasFocus()) {
      if (!active && !blocked()) visit.activeMs += Math.min(now - lastTick, 1000);
      visit.lastActivity = now;
      set('theniya_welcome_visit', JSON.stringify(visit));
    }
    lastTick = now;
    if (active && blocked()) close(false);
    refresh();
    if (welcomeDecision(state()).automatic) open(false);
  }, 250);
  window.addEventListener('pagehide', () => { stopped = true; clearInterval(timer); });
  window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
  refresh();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWelcome, { once: true });
  else initWelcome();
}
